import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  createIngrediente,
  deleteIngrediente,
  listIngredientes,
  updateIngrediente,
} from "../../services/supabase/ingredientesService";
import { isSupabaseConfigured, missingSupabaseConfig } from "../../services/supabase/config";
import { useAuth } from "./AuthContext";

const LOCAL_STORAGE_KEY = "mozzoapp:ingredientes";
const IngredientesContext = createContext();

const loadLocalIngredientes = () => {
  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (error) {
    console.error("No pudimos leer los ingredientes locales:", error);
    return [];
  }
};

const createLocalId = () =>
  `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const saveLocalIngredientes = (ingredientes) => {
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(ingredientes));
  } catch (error) {
    console.error("No pudimos guardar los ingredientes locales:", error);
  }
};

export const useIngredientes = () => {
  const context = useContext(IngredientesContext);
  if (!context) {
    throw new Error("useIngredientes debe usarse dentro de un IngredientesProvider");
  }
  return context;
};

export const IngredientesProvider = ({ children }) => {
  const { currentCompany, session, isCreatorMode } = useAuth();
  const userId = session?.user?.id;
  const companyId = currentCompany?.id;
  const loadRequestRef = useRef(0);
  const loadedScopeRef = useRef("");
  const [ingredientes, setIngredientesState] = useState([]);
  const [loadingIngredientes, setLoadingIngredientes] = useState(true);
  const [savingIngredientes, setSavingIngredientes] = useState(false);
  const [syncError, setSyncError] = useState("");
  const [storageMode, setStorageMode] = useState(isSupabaseConfigured ? "supabase" : "local");

  const setIngredientes = useCallback((nextValue) => {
    setIngredientesState((prev) =>
      typeof nextValue === "function" ? nextValue(prev) : nextValue
    );
  }, []);

  const cargarIngredientes = useCallback(async () => {
    const request = ++loadRequestRef.current;
    const scope = !isSupabaseConfigured || isCreatorMode ? "local" : `${companyId || ""}:${userId || ""}`;
    if (loadedScopeRef.current !== scope) {
      loadedScopeRef.current = scope;
      setIngredientesState([]);
    }
    if (!isSupabaseConfigured || isCreatorMode) {
      setStorageMode("local");
      setSyncError("");
      setIngredientesState(loadLocalIngredientes());
      setLoadingIngredientes(false);
      return;
    }

    if (!userId || !companyId) {
      setIngredientesState([]);
      setStorageMode("supabase");
      setSyncError("");
      setLoadingIngredientes(false);
      return;
    }

    setLoadingIngredientes(true);
    setSyncError("");

    try {
      const data = await listIngredientes(companyId, { includeImages: false, imageScope: userId });
      if (request !== loadRequestRef.current) return;
      setIngredientesState(data);
      setStorageMode("supabase");
      setSyncError("");
    } catch (error) {
      if (request !== loadRequestRef.current) return;
      setStorageMode("supabase");
      setSyncError(error.message);
    } finally {
      if (request === loadRequestRef.current) setLoadingIngredientes(false);
    }
  }, [companyId, isCreatorMode, userId]);

  useEffect(() => {
    cargarIngredientes();
    return () => { loadRequestRef.current += 1; };
  }, [cargarIngredientes]);

  useEffect(() => {
    if (storageMode === "local") {
      saveLocalIngredientes(ingredientes);
    }
  }, [ingredientes, storageMode]);

  const agregarIngrediente = useCallback(
    async (payload) => {
      setSavingIngredientes(true);

      try {
        if (storageMode === "supabase" && isSupabaseConfigured && !isCreatorMode) {
          const created = await createIngrediente(currentCompany?.id, payload);
          setIngredientesState((prev) => [{ ...created, imageScope: userId }, ...prev]);
          return created;
        }

        const localItem = {
          ...payload,
          id: createLocalId(),
          createdAt: new Date().toISOString(),
        };

        setIngredientesState((prev) => [localItem, ...prev]);
        return localItem;
      } catch (error) {
        if (storageMode === "supabase") {
          setSyncError(error.message);
          throw error;
        }

        const localItem = {
          ...payload,
          id: createLocalId(),
          createdAt: new Date().toISOString(),
        };

        setIngredientesState((prev) => [localItem, ...prev]);
        return localItem;
      } finally {
        setSavingIngredientes(false);
      }
    },
    [currentCompany?.id, isCreatorMode, storageMode, userId]
  );

  const actualizarIngrediente = useCallback(
    async (id, payload) => {
      setSavingIngredientes(true);

      try {
        if (storageMode === "supabase" && isSupabaseConfigured && !isCreatorMode) {
          const updated = await updateIngrediente(currentCompany?.id, id, payload);
          setIngredientesState((prev) =>
            prev.map((ingrediente) => (ingrediente.id === id ? { ...updated, imageScope: userId } : ingrediente))
          );
          return updated;
        }

        const localUpdated = { ...payload, id };
        setIngredientesState((prev) =>
          prev.map((ingrediente) => (ingrediente.id === id ? localUpdated : ingrediente))
        );
        return localUpdated;
      } catch (error) {
        if (storageMode === "supabase") {
          setSyncError(error.message);
          throw error;
        }

        const localUpdated = { ...payload, id };
        setIngredientesState((prev) =>
          prev.map((ingrediente) => (ingrediente.id === id ? localUpdated : ingrediente))
        );
        return localUpdated;
      } finally {
        setSavingIngredientes(false);
      }
    },
    [currentCompany?.id, isCreatorMode, storageMode, userId]
  );

  const eliminarIngrediente = useCallback(
    async (id) => {
      setSavingIngredientes(true);

      try {
        if (storageMode === "supabase" && isSupabaseConfigured && !isCreatorMode) {
          await deleteIngrediente(currentCompany?.id, id);
        }

        setIngredientesState((prev) =>
          prev.filter((ingrediente) => ingrediente.id !== id)
        );
      } catch (error) {
        if (storageMode === "supabase") {
          setSyncError(error.message);
          throw error;
        }

        setIngredientesState((prev) =>
          prev.filter((ingrediente) => ingrediente.id !== id)
        );
      } finally {
        setSavingIngredientes(false);
      }
    },
    [currentCompany?.id, isCreatorMode, storageMode]
  );

  const connectionMessage = useMemo(() => {
    if (storageMode === "supabase") {
      if (syncError) {
        return `No pudimos sincronizar los ingredientes. ${syncError}`;
      }

      if (currentCompany?.name) {
        return `Conectado a Supabase para la empresa ${currentCompany.name}.`;
      }

      return "Conectado a Supabase para ingredientes.";
    }

    if (!isSupabaseConfigured) {
      return `Modo local activo. Falta configurar: ${missingSupabaseConfig.join(", ")}.`;
    }

    if (isCreatorMode) {
      return "Modo creador activo. Estas trabajando en local para editar sin login.";
    }

    return "Modo local activo para seguir probando la app.";
  }, [currentCompany?.name, isCreatorMode, storageMode, syncError]);

  const value = useMemo(
    () => ({
      ingredientes,
      setIngredientes,
      cargarIngredientes,
      agregarIngrediente,
      actualizarIngrediente,
      eliminarIngrediente,
      loadingIngredientes,
      savingIngredientes,
      storageMode,
      syncError,
      connectionMessage,
    }),
    [
      agregarIngrediente,
      actualizarIngrediente,
      cargarIngredientes,
      connectionMessage,
      eliminarIngrediente,
      ingredientes,
      loadingIngredientes,
      savingIngredientes,
      setIngredientes,
      storageMode,
      syncError,
    ]
  );

  return (
    <IngredientesContext.Provider value={value}>
      {children}
    </IngredientesContext.Provider>
  );
};
